/* import __COLOCATED_TEMPLATE__ from './bulk-schedule-agent-modal.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import moment from 'moment-timezone';

interface AgentOption {
  label: string;
  key: string;
}

interface Args {
  onClose: () => void;
  onSubmit: (
    agentTypes: string[],
    availabilityAction: string,
    scheduledAt: string,
    revertAt?: string,
  ) => void;
  isBulkActionRunning: boolean;
}

interface Signature {
  Args: Args;
}

export default class BulkScheduleAgentModal extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;

  @tracked selectedAgentKeys: Set<string> = new Set(['chatbot', 'copilot']);
  @tracked selectedAction: 'enable' | 'disable' = 'enable';
  @tracked scheduledDate = this.defaultScheduledDate;
  @tracked showRevertAt = false;
  @tracked revertAt = this.defaultRevertAt;

  get canShowSalesAgentToggle(): boolean {
    return this.appService.app.canUseFinSalesAgent;
  }

  get canShowEcommerceAgentToggle(): boolean {
    return this.appService.app.canUseFeature('fin-ecommerce-agent');
  }

  get serviceAgentLabel(): string {
    if (this.canShowSalesAgentToggle || this.canShowEcommerceAgentToggle) {
      return this.appService.app.canUseStandalone
        ? this.intl.t(
            'knowledge-hub.content-editor.shared.visibility-section.standalone.fin-ai-agent-for-service',
          )
        : this.intl.t(
            'knowledge-hub.content-editor.shared.visibility-section.fin-ai-agent-for-service',
          );
    }
    return this.appService.app.canUseStandalone
      ? this.intl.t(
          'knowledge-hub.content-editor.shared.visibility-section.standalone.fin-ai-agent',
        )
      : this.intl.t('knowledge-hub.content-editor.shared.visibility-section.fin-ai-agent');
  }

  get agentOptions(): AgentOption[] {
    return [
      {
        label: this.serviceAgentLabel,
        key: 'chatbot',
      },
      {
        label: this.appService.app.canUseStandalone
          ? this.intl.t(
              'knowledge-hub.content-editor.shared.visibility-section.standalone.fin-ai-copilot',
            )
          : this.intl.t('knowledge-hub.content-editor.shared.visibility-section.fin-ai-copilot'),
        key: 'copilot',
      },
    ];
  }

  get today() {
    return new Date();
  }

  get agentSelectItems() {
    return this.agentOptions.map((opt) => ({
      value: opt.key,
      text: opt.label,
      isSelected: this.selectedAgentKeys.has(opt.key),
    }));
  }

  get selectedAgentsLabel() {
    let selected = this.agentOptions.filter((opt) => this.selectedAgentKeys.has(opt.key));
    if (selected.length === 0) {
      return this.intl.t(
        'knowledge-hub.filterable-list.bulk-actions.schedule-agent.select-ai-product',
      );
    }
    let labels = selected.map((opt) => opt.label);
    if (labels.length === 1) {
      return labels[0];
    }
    return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
  }

  get defaultScheduledDate() {
    return moment
      .tz(this.appService.app.timezone)
      .add(1, 'day')
      .hour(9)
      .minute(0)
      .second(0)
      .millisecond(0)
      .toDate();
  }

  get defaultRevertAt() {
    return moment(this.scheduledDate).add(1, 'hour').second(0).millisecond(0).toDate();
  }

  get scheduledDateError() {
    if (this.scheduledDate <= new Date()) {
      return this.intl.t(
        'knowledge-hub.filterable-list.bulk-actions.schedule-agent.start-date-error',
      );
    }
    return null;
  }

  get revertAtError() {
    if (this.showRevertAt && this.revertAt <= this.scheduledDate) {
      return this.intl.t(
        'knowledge-hub.filterable-list.bulk-actions.schedule-agent.end-date-error',
      );
    }
    return null;
  }

  get canSave() {
    return (
      this.selectedAgentKeys.size > 0 &&
      !this.args.isBulkActionRunning &&
      !this.scheduledDateError &&
      !this.revertAtError
    );
  }

  @action onAgentSelectionChange(selectedValues: string[]) {
    this.selectedAgentKeys = new Set(selectedValues);
  }

  @action onActionChange(value: 'enable' | 'disable') {
    this.selectedAction = value;
  }

  @action onScheduledDateChange(date: Date) {
    this.scheduledDate = date;
  }

  @action onRevertAtChange(date: Date) {
    this.revertAt = date;
  }

  @action toggleRevertAt() {
    this.showRevertAt = !this.showRevertAt;
  }

  @action submit() {
    if (!this.canSave) {
      return;
    }
    this.args.onSubmit(
      Array.from(this.selectedAgentKeys),
      this.selectedAction,
      this.scheduledDate.toISOString(),
      this.showRevertAt ? this.revertAt.toISOString() : undefined,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::FilterableList::BulkActions::BulkScheduleAgentModal': typeof BulkScheduleAgentModal;
    'knowledge-hub/filterable-list/bulk-actions/bulk-schedule-agent-modal': typeof BulkScheduleAgentModal;
  }
}
